<template>
  <div class="content">
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">
            {{ $t('member_admin.title') }}
          </h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </div>
        <div class="col-lg-6 col-5 text-right" v-if="canEdit">
          <base-button size="md" type="neutral" @click="openCreate">
            <i class="fa fa-plus"></i> {{ $t('add') || 'Create' }}
          </base-button>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6">
      <div>
        <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
          <template slot="header">
            <div class="row">
              <div class="col-md-6">
                <base-input
                  v-model="q"
                  prepend-icon="fas fa-search"
                  :placeholder="$t('member_admin.search_placeholder')"
                  @keyup.enter.native="search"
                ></base-input>
              </div>
              <div class="col-md-2">
                <base-input>
                  <el-select v-model="status" :placeholder="$t('status')" clearable @change="search" class="w-100">
                    <el-option :label="$t('member_admin.status_not_verify')" value="0"></el-option>
                    <el-option :label="$t('member_admin.status_normal')" value="1"></el-option>
                    <el-option :label="$t('member_admin.status_block')" value="2"></el-option>
                    <el-option :label="$t('member_admin.status_registered_by_admin')" value="3"></el-option>
                  </el-select>
                </base-input>
              </div>
              <div class="col-md-4 text-right">
                <base-button size="sm" type="primary" @click="search">
                  <i class="fa fa-search"></i> {{ $t('search') }}
                </base-button>
                <base-button size="sm" type="success" :loading="exporting" @click="exportExcel">
                  <i class="fa fa-file-excel"></i> {{ $t('member_admin.export_excel') }}
                </base-button>
                <base-button v-if="isAdmin" size="sm" type="warning" @click="openCalcSpending">
                  <i class="fa fa-calculator"></i> {{ $t('member_admin.calc_spending') }}
                </base-button>
              </div>
            </div>
            <!-- Row 1: created_at + birthday daterange. Birthday is still a
                 calendar-day filter under the hood — backend reads only the
                 MM-DD from the YYYY-MM-DD strings, so wrap-around (e.g. Dec→Jan)
                 across a multi-year pick still works. -->
            <div class="row mt-2 align-items-end">
              <div class="col-md-6">
                <base-input :label="$t('member_admin.created_range') || 'Created date'">
                  <el-date-picker
                    v-model="createdRange"
                    type="daterange"
                    range-separator="—"
                    :start-placeholder="$t('member_admin.created_from') || 'From'"
                    :end-placeholder="$t('member_admin.created_to') || 'To'"
                    value-format="yyyy-MM-dd"
                    format="yyyy-MM-dd"
                    clearable
                    @change="search"
                    style="width: 100%"
                  ></el-date-picker>
                </base-input>
              </div>
              <div class="col-md-6">
                <base-input :label="$t('member_admin.birthday_range') || 'Birthday range'">
                  <el-date-picker
                    v-model="birthdayRange"
                    type="daterange"
                    range-separator="—"
                    :start-placeholder="$t('member_admin.birthday_from') || 'From (DD/MM)'"
                    :end-placeholder="$t('member_admin.birthday_to') || 'To (DD/MM)'"
                    value-format="yyyy-MM-dd"
                    format="dd/MM"
                    clearable
                    @change="search"
                    style="width: 100%"
                  ></el-date-picker>
                </base-input>
              </div>
            </div>
            <!-- Member type filter — limits the list (and Export) to one source. -->
            <div class="row mt-2 align-items-end">
              <div class="col-md-3">
                <base-input :label="$t('member_admin.member_type') || 'Member type'">
                  <el-select v-model="memberType" clearable @change="search" style="width: 100%">
                    <el-option :label="$t('member_admin.member_type_all') || 'All'" value=""></el-option>
                    <el-option :label="$t('member_admin.member_type_company') || 'Company'" :value="1"></el-option>
                    <el-option :label="$t('member_admin.member_type_agent') || 'Agent'" :value="2"></el-option>
                    <el-option :label="$t('member_admin.member_type_platform') || 'Platform'" :value="3"></el-option>
                  </el-select>
                </base-input>
              </div>
            </div>
            <!-- Row 2: spending + order_count ranges. 0/0 = no filter. -->
            <div class="row mt-2 align-items-end">
              <div class="col-md-3">
                <base-input
                  type="number"
                  min="0"
                  v-model.number="spending_min"
                  :label="$t('member_admin.spending_min')"
                  :placeholder="$t('member_admin.spending_unset_hint')"
                  @keyup.enter.native="search"
                ></base-input>
              </div>
              <div class="col-md-3">
                <base-input
                  type="number"
                  min="0"
                  v-model.number="spending_max"
                  :label="$t('member_admin.spending_max')"
                  :placeholder="$t('member_admin.spending_unset_hint')"
                  @keyup.enter.native="search"
                ></base-input>
              </div>
              <div class="col-md-3">
                <base-input
                  type="number"
                  min="0"
                  v-model.number="order_count_min"
                  :label="$t('member_admin.order_count_min')"
                  :placeholder="$t('member_admin.spending_unset_hint')"
                  @keyup.enter.native="search"
                ></base-input>
              </div>
              <div class="col-md-3">
                <base-input
                  type="number"
                  min="0"
                  v-model.number="order_count_max"
                  :label="$t('member_admin.order_count_max')"
                  :placeholder="$t('member_admin.spending_unset_hint')"
                  @keyup.enter.native="search"
                ></base-input>
              </div>
            </div>
            <!-- Clears for the number-range filters (date pickers have their
                 own X icon via clearable). Only render when something is set. -->
            <div class="row mt-1 align-items-end"
                 v-if="(spending_min || 0) > 0 || (spending_max || 0) > 0 ||
                       (order_count_min || 0) > 0 || (order_count_max || 0) > 0">
              <div class="col-md-6">
                <base-button v-if="(spending_min || 0) > 0 || (spending_max || 0) > 0"
                             size="sm" type="default" outline @click="clearSpendingRange">
                  <i class="fa fa-times"></i> {{ $t('member_admin.spending_clear') }}
                </base-button>
                <base-button v-if="(order_count_min || 0) > 0 || (order_count_max || 0) > 0"
                             size="sm" type="default" outline @click="clearOrderCountRange" class="ml-2">
                  <i class="fa fa-times"></i> {{ $t('member_admin.order_count_clear') }}
                </base-button>
              </div>
            </div>
          </template>

          <div>
            <el-table
              :data="rows"
              row-key="id"
              header-row-class-name="thead-light"
              v-loading="loading"
              :default-sort="{prop: 'total_spending', order: 'descending'}"
              @sort-change="onSortChange"
            >
              <el-table-column :label="$t('member_admin.fname')" min-width="160">
                <div slot-scope="{row}">
                  <a href="javascript:void(0)" class="font-weight-bold" @click="open(row.id)">
                    {{ row.fname }} {{ row.lname }}
                  </a>
                </div>
              </el-table-column>
              <el-table-column :label="$t('member_admin.phone')" prop="phone" min-width="110" sortable="custom"></el-table-column>
              <el-table-column :label="$t('member_admin.birthday')" prop="birthday" min-width="80" sortable="custom">
                <div slot-scope="{row}">{{ fmtBirthdayBE(row.birthday) }}</div>
              </el-table-column>
              <el-table-column :label="$t('member_admin.age')" prop="birthday" width="100" align="right">
                <div slot-scope="{row}">{{ ageFromBirthday(row.birthday) }}</div>
              </el-table-column>
              <el-table-column :label="$t('member_admin.total_spending')" prop="total_spending" width="130" align="right" sortable="custom">
                <div slot-scope="{row}" class="font-weight-bold">{{ fmtNum(row.total_spending) }}</div>
              </el-table-column>
              <el-table-column :label="$t('member_admin.in_transit_spending')" prop="in_transit_spending" width="130" align="right" sortable="custom">
                <div slot-scope="{row}" class="text-warning">{{ fmtNum(row.in_transit_spending) }}</div>
              </el-table-column>
              <el-table-column :label="$t('member_admin.success_spending')" prop="success_spending" width="130" align="right" sortable="custom">
                <div slot-scope="{row}" class="text-success">{{ fmtNum(row.success_spending) }}</div>
              </el-table-column>
              <el-table-column :label="$t('member_admin.return_cancel_spending')" prop="return_cancel_spending" width="130" align="right" sortable="custom">
                <div slot-scope="{row}" class="text-danger">{{ fmtNum(row.return_cancel_spending) }}</div>
              </el-table-column>
              <el-table-column :label="$t('member_admin.order_count')" prop="order_count" width="110" align="right" sortable="custom">
                <div slot-scope="{row}">{{ fmtNum(row.order_count) }}</div>
              </el-table-column>
              <el-table-column :label="$t('member_admin.member_type') || 'Type'" prop="member_type" width="130" align="center">
                <div slot-scope="{row}">
                  <span class="badge badge-pill badge-lg" :style="memberTypeStyle(row.member_type)">
                    {{ memberTypeLabel(row.member_type) }}
                  </span>
                </div>
              </el-table-column>
              <el-table-column :label="$t('status')" prop="status" width="180" align="center">
                <div slot-scope="{row}">
                  <badge :type="statusBadge(row.status)" class="badge-lg">{{ statusLabel(row.status) }}</badge>
                </div>
              </el-table-column>
            </el-table>
          </div>

          <div
            slot="footer"
            class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
            v-if="total > 0"
          >
            <div>
              <p class="card-category">
                {{ $t('data') }} {{ from + 1 }} {{ $t('to') }} {{ to }} {{ $t('of') }} {{ total }}
              </p>
            </div>
            <el-pagination
              background
              layout="sizes, prev, pager, next, jumper"
              :total="total"
              :page-size="perPage"
              :page-sizes="[15, 30, 50, 100]"
              :current-page="page"
              @current-change="handlePageChange"
              @size-change="handleSizeChange"
            ></el-pagination>
          </div>
        </card>
      </div>
    </div>

    <!-- Create member modal -->
    <modal :show.sync="showCreate" body-classes="p-0" modal-classes="modal-lg">
      <card type="secondary" header-classes="bg-white pb-5" body-classes="px-lg-5 py-lg-5" class="border-0 mb-0">
        <h3>{{ $t('member_admin.create_title') }}</h3>
        <p class="text-muted">{{ $t('member_admin.create_subtitle') }}</p>

        <div class="row">
          <div class="col-md-6">
            <base-input :label="$t('member_admin.first_name') + ' *'" v-model="form.fname" required></base-input>
          </div>
          <div class="col-md-6">
            <base-input :label="$t('member_admin.last_name')" v-model="form.lname"></base-input>
          </div>
        </div>
        <div class="row">
          <div class="col-md-6">
            <base-input :label="$t('member_admin.phone') + ' *'" v-model="form.phone" required></base-input>
          </div>
          <div class="col-md-6">
            <base-input :label="$t('member_admin.email')" type="email" v-model="form.email"></base-input>
          </div>
        </div>
        <div class="row">
          <div class="col-md-6">
            <base-input :label="$t('member_admin.birthday')" type="date" v-model="form.birthday"></base-input>
          </div>
          <div class="col-md-3">
            <div class="form-group">
              <label class="form-control-label">{{ $t('member_admin.sex') }}</label>
              <el-select v-model="form.sex" class="w-100">
                <el-option :value="0" label="-"></el-option>
                <el-option :value="1" :label="$t('member_admin.sex_male')"></el-option>
                <el-option :value="2" :label="$t('member_admin.sex_female')"></el-option>
              </el-select>
            </div>
          </div>
          <div class="col-md-3">
            <div class="form-group">
              <label class="form-control-label">{{ $t('status') }}</label>
              <!-- Status codes mirror constants/const.go (MOBILE_STATUS_*) so
                   the dropdown matches the badge/filter mapping used in the
                   list and statusLabel(). 0=not_verify, 1=normal, 2=block,
                   3=registered_by_admin. -->
              <el-select v-model="form.status" class="w-100">
                <el-option :value="0" :label="$t('member_admin.status_not_verify')"></el-option>
                <el-option :value="1" :label="$t('member_admin.status_normal')"></el-option>
                <el-option :value="2" :label="$t('member_admin.status_block')"></el-option>
                <el-option :value="3" :label="$t('member_admin.status_registered_by_admin')"></el-option>
              </el-select>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-md-12">
            <base-input :label="$t('member_admin.address')" v-model="form.address"></base-input>
          </div>
        </div>

        <div class="row">
          <div class="col-md-12">
            <div class="form-group">
              <label class="form-control-label">{{ $t('member_admin.address_search') }}</label>
              <base-input
                v-model="addrQuery"
                :placeholder="$t('member_admin.address_search')"
                @input="onAddrChange"
                @keydown.down.native="onAddrArrowDown"
                @keydown.up.native="onAddrArrowUp"
                @keydown.enter.native="onAddrEnter"
              ></base-input>
              <ul v-show="addrOpen" class="autocomplete-results">
                <li
                  v-for="(result, i) in filteredAddress"
                  :key="i"
                  @click="setAddrResult(result)"
                  class="autocomplete-result"
                  :class="{ 'is-active': i === addrCounter }"
                >
                  <template v-if="result.ProvinceID == 10">
                    แขวง{{ result.District }} เขต{{ result.City }} จังหวัด{{ result.Province }} {{ result.Zipcode }}
                  </template>
                  <template v-else>
                    ต.{{ result.District }} อ.{{ result.City }} จ.{{ result.Province }} {{ result.Zipcode }}
                  </template>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-3">
            <base-input :label="$t('member_admin.subdistrict')" v-model="form.subdistrict" readonly></base-input>
          </div>
          <div class="col-md-3">
            <base-input :label="$t('member_admin.district')" v-model="form.district" readonly></base-input>
          </div>
          <div class="col-md-3">
            <base-input :label="$t('member_admin.province')" v-model="form.province" readonly></base-input>
          </div>
          <div class="col-md-3">
            <base-input :label="$t('member_admin.postcode')" v-model="form.postcode" readonly></base-input>
          </div>
        </div>

        <div class="text-right mt-3">
          <base-button type="link" @click="showCreate = false" :disabled="creating">{{ $t('cancel') }}</base-button>
          <base-button type="primary" :disabled="!form.fname || !form.phone || creating" @click="submitCreate">
            <i class="fa fa-save"></i> {{ $t('save') }}
          </base-button>
        </div>
      </card>
    </modal>

    <!-- Recalculate total_spending — admin only.
         Two visual states in the same modal:
           - calc.status === null  → date pickers + Run button
           - else                  → progress view polling /status -->
    <modal :show.sync="showCalc" body-classes="p-0" modal-classes="modal-md">
      <card type="secondary" header-classes="bg-white pb-3" body-classes="px-lg-5 py-lg-4" class="border-0 mb-0">
        <h3 class="mb-1">{{ $t('member_admin.calc_spending_title') }}</h3>
        <p class="text-muted small">{{ $t('member_admin.calc_spending_hint') }}</p>

        <!-- Date pickers (idle state) -->
        <template v-if="!calc.status">
          <div class="row">
            <div class="col-md-6">
              <base-input :label="$t('member_admin.calc_date_from')">
                <el-date-picker
                  v-model="calc.dateFrom"
                  type="date"
                  value-format="yyyy-MM-dd"
                  format="yyyy-MM-dd"
                  :placeholder="$t('member_admin.calc_date_from')"
                  style="width: 100%"
                ></el-date-picker>
              </base-input>
            </div>
            <div class="col-md-6">
              <base-input :label="$t('member_admin.calc_date_to')">
                <el-date-picker
                  v-model="calc.dateTo"
                  type="date"
                  value-format="yyyy-MM-dd"
                  format="yyyy-MM-dd"
                  :placeholder="$t('member_admin.calc_date_to')"
                  style="width: 100%"
                ></el-date-picker>
              </base-input>
            </div>
          </div>

          <div class="text-right mt-3">
            <base-button type="link" @click="showCalc = false" :disabled="calc.starting">{{ $t('cancel') }}</base-button>
            <base-button
              type="warning"
              :disabled="!calc.dateFrom || !calc.dateTo || calc.starting"
              :loading="calc.starting"
              @click="runCalcSpending"
            >
              <i class="fa fa-calculator"></i> {{ $t('member_admin.calc_spending_run') }}
            </base-button>
          </div>
        </template>

        <!-- Progress view (running / done / error) -->
        <template v-else>
          <div class="text-muted small mb-2">
            <b>{{ calc.status.date_from }}</b> → <b>{{ calc.status.date_to }}</b>
          </div>

          <div v-if="calc.status.phase === 'starting'" class="alert alert-info py-2">
            <i class="fa fa-spinner fa-spin mr-2"></i>
            {{ $t('member_admin.calc_started') }}
          </div>

          <div v-else-if="calc.status.phase === 'querying'">
            <div class="text-sm mb-1">
              <i class="fa fa-database mr-1"></i>
              {{ $t('member_admin.calc_processing_date') }}: <b>{{ calc.status.current_date || '-' }}</b>
              <span class="text-muted ml-2">({{ calc.status.chunk_no }}/{{ calc.status.chunk_total }})</span>
            </div>
            <div class="progress" style="height: 18px;">
              <div class="progress-bar bg-info progress-bar-striped progress-bar-animated"
                   role="progressbar"
                   :style="{ width: (calc.status.progress_pct || 0) + '%' }">
                {{ calc.status.progress_pct || 0 }}%
              </div>
            </div>
          </div>

          <div v-else-if="calc.status.phase === 'matching'" class="alert alert-info py-2">
            <i class="fa fa-search fa-spin mr-2"></i>
            {{ $t('member_admin.calc_matching') }}
            <span class="text-muted ml-2">({{ calc.status.phones_seen }} {{ $t('member_admin.calc_phones_seen') }})</span>
          </div>

          <div v-else-if="calc.status.phase === 'updating'">
            <div class="text-sm mb-1">
              <i class="fa fa-sync fa-spin mr-1"></i>
              {{ $t('member_admin.calc_updating') }}:
              <b>{{ calc.status.updated_so_far }}</b> / {{ calc.status.members_matched }}
              <span class="text-muted ml-2">
                ({{ calc.status.members_matched }} {{ $t('member_admin.calc_of') }}
                 {{ calc.status.phones_seen }} {{ $t('member_admin.calc_phones_seen') }})
              </span>
            </div>
            <div class="progress" style="height: 18px;">
              <div class="progress-bar bg-warning progress-bar-striped progress-bar-animated"
                   role="progressbar"
                   :style="{ width: (calc.status.progress_pct || 0) + '%' }">
                {{ calc.status.progress_pct || 0 }}%
              </div>
            </div>
          </div>

          <div v-else-if="calc.status.phase === 'done'" class="alert alert-success py-2">
            <i class="fa fa-check-circle mr-2"></i>
            {{ $t('member_admin.calc_spending_done') }} —
            {{ $t('member_admin.calc_spending_updated') }}: <b>{{ calc.status.updated_members }}</b>,
            {{ $t('member_admin.calc_spending_total') }}: <b>{{ fmtNum(calc.status.total_spent) }}</b>
          </div>

          <div v-else-if="calc.status.phase === 'error'" class="alert alert-danger py-2">
            <i class="fa fa-exclamation-triangle mr-2"></i>
            {{ calc.status.error || $t('member_admin.calc_error') }}
          </div>

          <div class="text-muted small mt-2">
            <i class="fa fa-clock mr-1"></i>
            {{ $t('member_admin.calc_duration') }}: {{ fmtDuration(calc.status.duration_seconds) }}
          </div>

          <div class="text-right mt-3">
            <base-button
              type="link"
              :disabled="calc.status.running"
              @click="closeCalc"
            >
              {{ calc.status.running ? $t('member_admin.calc_running_hide') : $t('close') }}
            </base-button>
          </div>
        </template>
      </card>
    </modal>
  </div>
</template>

<script>
import { Table, TableColumn, Select, Option, Pagination, DatePicker } from "element-ui";
import { Badge } from "@/components";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import MemberProvider from "@/resource/member";
import swal from "sweetalert2";
import { mapActions, mapGetters } from "vuex";

const member = new MemberProvider();

export default {
  // Named so DashboardLayout's <keep-alive include> can target it. Keeping the
  // instance alive preserves page / q / status / rows / scroll when the user
  // opens a member detail and clicks Back.
  name: "MemberList",
  components: {
    RouteBreadCrumb,
    Badge,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
    [Pagination.name]: Pagination,
    [DatePicker.name]: DatePicker,
  },
  data() {
    return {
      loading: false,
      exporting: false,
      rows: [],
      q: "",
      status: "",
      // Birthday calendar-day range — el-date-picker daterange gives us a
      // [startISO, endISO] tuple as YYYY-MM-DD strings. Backend strips to
      // MM-DD so the year selection is cosmetic; wrap-around works.
      birthdayRange: null,
      // total_spending range filter — both 0 means "no filter".
      spending_min: 0,
      spending_max: 0,
      // order_count range filter — same 0/0 semantics.
      order_count_min: 0,
      order_count_max: 0,
      // created_at range — el-date-picker daterange returns [startISO, endISO]
      // formatted as YYYY-MM-DD strings. Empty / null = no filter.
      createdRange: null,
      // member_type filter — 0/"" = all; 1 = company; 2 = agent; 3 = platform.
      memberType: "",
      page: 1,
      perPage: 15,
      total: 0,
      // Server-side sort state. Defaults match the el-table default-sort prop
      // and the backend's new "total_spending desc" default. Updated via
      // @sort-change → onSortChange().
      sortBy: "total_spending",
      sortDir: "desc",
      // Admin-only "Recalculate total_spending" modal state.
      //   status === null  → idle (show date pickers)
      //   status !== null  → progress view, status holds the polled snapshot
      //   pollTimer        → setInterval id while polling
      //   starting         → guards the Run button while POST is in flight
      showCalc: false,
      calc: {
        dateFrom: "",
        dateTo: "",
        starting: false,
        status: null,
        pollTimer: null,
      },
      showCreate: false,
      creating: false,
      form: this.emptyForm(),
      addrQuery: "",
      addrOpen: false,
      addrCounter: -1,
    };
  },
  computed: {
    ...mapGetters({
      canEdit: "Auth/isEditMember",
      isAdmin: "Auth/isAdmin",
      FullAddr: "Order/getAddress",
    }),
    from() { return (this.page - 1) * this.perPage; },
    to() { return Math.min(this.page * this.perPage, this.total); },
    filteredAddress() {
      if (!this.addrQuery) return [];
      const key = this.addrQuery.split(" ");
      const result = (this.FullAddr || []).filter((item) => {
        if (key.length == 1) {
          return item.District.includes(key[0]) || item.Zipcode == key[0];
        }
        if (key.length == 2) {
          return (item.District.includes(key[0]) || item.Zipcode == key[0]) && item.City.includes(key[1]);
        }
        if (key.length >= 3) {
          return (item.District.includes(key[0]) || item.Zipcode == key[0]) && item.City.includes(key[1]) && item.Province.includes(key[2]);
        }
        return false;
      });
      return result.slice(0, 15);
    },
  },
  mounted() {
    this.loadData();
    if ((this.FullAddr || []).length === 0) {
      this.listFullAddr();
    }
  },
  beforeDestroy() {
    this.stopCalcPolling();
  },
  methods: {
    ...mapActions({
      listFullAddr: "Order/listAddressV2",
    }),
    // Status codes mirror constants/const.go:
    //   0 = MOBILE_STATUS_NOT_VERIFY
    //   1 = MOBILE_STATUS_NORMAL_APP_VERIFED
    //   2 = MOBILE_STATUS_BLOCK
    //   3 = MOBILE_STATUS_REGISTERED_BY_ADMIN
    // Soft pastel palette for the member-type badge — the solid
    // bootstrap `.badge-primary/success/warning` were too loud next to
    // the regular status column, so we override with inline pastels.
    memberTypeStyle(t) {
      const n = Number(t);
      if (n === 1) return { backgroundColor: "#dbe9ff", color: "#1e3a8a" }; // company  — soft blue
      if (n === 2) return { backgroundColor: "#d8f1de", color: "#166534" }; // agent    — soft green
      if (n === 3) return { backgroundColor: "#ffe6c7", color: "#92400e" }; // platform — soft orange
      return { backgroundColor: "#e9ecef", color: "#495057" };
    },
    memberTypeLabel(t) {
      const n = Number(t);
      if (n === 1) return this.$t("member_admin.member_type_company") || "Company";
      if (n === 2) return this.$t("member_admin.member_type_agent") || "Agent";
      if (n === 3) return this.$t("member_admin.member_type_platform") || "Platform";
      return "-";
    },
    statusBadge(s) {
      const n = Number(s);
      if (n === 0) return "warning";  // Not verified / pending — yellow
      if (n === 1) return "success";  // Normal — green
      if (n === 2) return "danger";   // Block — red
      if (n === 3) return "info";     // Registered by admin — blue
      return "default";
    },
    // Render a birthday from the API (e.g. "1998-06-10 00:00:00") as
    // "<day> <Thai month> <BE year>" — e.g. "10 มิถุนายน 2541". Returns "-"
    // for missing values or the schema-default placeholder (1900-01-01).
    fmtBirthdayBE(b) {
      if (!b) return "-";
      const ymd = String(b).substring(0, 10);
      const [ys, ms, ds] = ymd.split("-");
      const y = parseInt(ys, 10);
      const m = parseInt(ms, 10);
      const d = parseInt(ds, 10);
      if (!y || !m || !d || y <= 1900) return "-";
      const months = [
        "มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน",
        "พฤษภาคม", "มิถุนายน", "กรกฎาคม", "สิงหาคม",
        "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม",
      ];
      return `${d} ${months[m - 1]} ${y + 543}`;
    },
    // Age (in completed years) from a birthday string. Returns "-" for
    // missing values or the 1900-01-01 placeholder.
    ageFromBirthday(b) {
      if (!b) return "-";
      const ymd = String(b).substring(0, 10);
      const [ys, ms, ds] = ymd.split("-");
      const y = parseInt(ys, 10);
      const m = parseInt(ms, 10);
      const d = parseInt(ds, 10);
      if (!y || !m || !d || y <= 1900) return "-";
      const today = new Date();
      let age = today.getFullYear() - y;
      const before = today.getMonth() + 1 < m
        || (today.getMonth() + 1 === m && today.getDate() < d);
      if (before) age--;
      return age >= 0 ? age : "-";
    },
    statusLabel(s) {
      const n = Number(s);
      if (n === 0) return this.$t("member_admin.status_not_verify");
      if (n === 1) return this.$t("member_admin.status_normal");
      if (n === 2) return this.$t("member_admin.status_block");
      if (n === 3) return this.$t("member_admin.status_registered_by_admin");
      return "-";
    },
    queryParams() {
      const p = {
        q: this.q,
        status: this.status,
        page: this.page,
        per_page: this.perPage,
        sort: this.sortBy,
        sort_dir: this.sortDir,
      };
      // Birthday range — only the MM-DD portion matters server-side, but
      // we still pass the full ISO so old query-string tooling parses.
      if (this.birthdayRange && this.birthdayRange.length === 2) {
        if (this.birthdayRange[0]) p.birthday_from = this.birthdayRange[0];
        if (this.birthdayRange[1]) p.birthday_to = this.birthdayRange[1];
      }
      // Spending range — backend skips the filter entirely when both are 0.
      const min = Number(this.spending_min) || 0;
      const max = Number(this.spending_max) || 0;
      if (min > 0 || max > 0) {
        p.spending_min = min;
        p.spending_max = max;
      }
      // Order-count range — same semantics.
      const cmin = Number(this.order_count_min) || 0;
      const cmax = Number(this.order_count_max) || 0;
      if (cmin > 0 || cmax > 0) {
        p.order_count_min = cmin;
        p.order_count_max = cmax;
      }
      // created_at window — only send when both ends are set so a half-open
      // range doesn't accidentally filter to "from today" / "until today".
      if (this.createdRange && this.createdRange.length === 2) {
        if (this.createdRange[0]) p.created_from = this.createdRange[0];
        if (this.createdRange[1]) p.created_to = this.createdRange[1];
      }
      // member_type filter — backend skips when 0 / empty.
      const mt = Number(this.memberType) || 0;
      if (mt > 0) p.member_type = mt;
      return p;
    },
    async loadData() {
      this.loading = true;
      try {
        const res = await member.AdminList(this.queryParams());
        const data = (res.data && res.data.Data && res.data.Data[0]) || {};
        this.rows = data.items || [];
        this.total = data.total || 0;
        // Sync page back from server in case it clamped (e.g. requested page beyond range).
        if (data.page) this.page = data.page;
        if (data.per_page) this.perPage = data.per_page;
      } catch (e) { console.log(e); }
      this.loading = false;
    },
    clearSpendingRange() {
      this.spending_min = 0;
      this.spending_max = 0;
      this.search();
    },
    clearOrderCountRange() {
      this.order_count_min = 0;
      this.order_count_max = 0;
      this.search();
    },
    clearBirthdayRange() {
      this.birthdayRange = null;
      this.search();
    },
    clearCreatedRange() {
      this.createdRange = null;
      this.search();
    },
    openCalcSpending() {
      // Default to current month if no values yet.
      if (!this.calc.dateFrom || !this.calc.dateTo) {
        const today = new Date();
        const first = new Date(today.getFullYear(), today.getMonth(), 1);
        const fmt = (d) =>
          `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        this.calc.dateFrom = fmt(first);
        this.calc.dateTo = fmt(today);
      }
      this.calc.status = null;
      this.showCalc = true;

      // Pre-check the server: if a job is already running, jump straight into
      // the progress view so a fresh page load doesn't re-prompt the staff.
      member.AdminCalculateSpendingStatus()
        .then((res) => {
          const datum = (res.data && res.data.Datum) || null;
          if (datum && datum.running) {
            this.calc.status = datum;
            this.startCalcPolling();
          }
        })
        .catch(() => {});
    },
    fmtNum(v) {
      if (v === null || v === undefined || v === "") return "0";
      return Number(v).toLocaleString("en-US");
    },
    fmtDuration(seconds) {
      const s = Math.max(0, Number(seconds || 0));
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      const sec = s % 60;
      if (h > 0) return `${h}h ${m}m ${sec}s`;
      if (m > 0) return `${m}m ${sec}s`;
      return `${sec}s`;
    },
    startCalcPolling() {
      this.stopCalcPolling();
      this.calc.pollTimer = setInterval(async () => {
        try {
          const res = await member.AdminCalculateSpendingStatus();
          const datum = (res.data && res.data.Datum) || {};
          this.calc.status = datum;
          if (!datum.running && (datum.phase === "done" || datum.phase === "error")) {
            this.stopCalcPolling();
            if (datum.phase === "done") {
              // Refresh the table so updated total_spending shows up.
              this.loadData();
            }
          }
        } catch (e) {
          console.log(e);
        }
      }, 1000);
    },
    stopCalcPolling() {
      if (this.calc.pollTimer) {
        clearInterval(this.calc.pollTimer);
        this.calc.pollTimer = null;
      }
    },
    closeCalc() {
      this.stopCalcPolling();
      this.showCalc = false;
      this.calc.status = null;
    },
    async runCalcSpending() {
      this.calc.starting = true;
      try {
        const res = await member.AdminCalculateSpending({
          date_from: this.calc.dateFrom,
          date_to: this.calc.dateTo,
        });
        // First-click happy path: backend returns 200 with phase=starting.
        // Switch to the progress view and start polling.
        const datum = (res.data && res.data.Datum) || {};
        this.calc.status = Object.assign(
          { running: true, phase: datum.phase || "starting", progress_pct: 0 },
          datum,
        );
        swal.fire({
          title: this.$t("member_admin.calc_started"),
          icon: "info",
          timer: 1500,
          showConfirmButton: false,
          buttonsStyling: false,
        });
        this.startCalcPolling();
      } catch (e) {
        // 409 = job already in progress. Switch into progress view using
        // the snapshot the backend returned.
        if (e.response && e.response.status === 409) {
          const datum = (e.response.data && e.response.data.Datum) || {};
          this.calc.status = Object.assign({ running: true }, datum);
          swal.fire({
            title: this.$t("member_admin.calc_already_running"),
            icon: "warning",
            timer: 2000,
            showConfirmButton: false,
            buttonsStyling: false,
          });
          this.startCalcPolling();
        } else {
          const msg = (e.response && e.response.data && (e.response.data.Cause || e.response.data.Msg)) || "Failed";
          swal.fire({
            title: msg,
            icon: "error",
            buttonsStyling: false,
            confirmButtonClass: "btn btn-danger btn-fill",
          });
        }
      }
      this.calc.starting = false;
    },
    async exportExcel() {
      this.exporting = true;
      try {
        const params = this.queryParams();
        // Pagination is irrelevant for the export.
        delete params.page;
        delete params.per_page;
        const res = await member.AdminExport(params);
        const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        // Filename from the Content-Disposition header if present, otherwise a sensible default.
        let filename = "members.xlsx";
        const cd = (res.headers && (res.headers["content-disposition"] || res.headers["Content-Disposition"])) || "";
        const match = /filename="?([^"]+)"?/i.exec(cd);
        if (match && match[1]) filename = match[1];
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        console.log(e);
        swal.fire({
          title: this.$t("member_admin.export_failed") || "Export failed",
          icon: "error",
          buttonsStyling: false,
          confirmButtonClass: "btn btn-danger btn-fill",
        });
      }
      this.exporting = false;
    },
    handlePageChange(page) {
      this.page = page;
      this.loadData();
    },
    handleSizeChange(size) {
      this.perPage = size;
      this.page = 1;
      this.loadData();
    },
    search() { this.page = 1; this.loadData(); },
    // el-table emits {column, prop, order} where order is "ascending"/"descending"/null.
    // null fires when the user clicks a third time to clear the arrow — we fall
    // back to total_spending desc (the backend default).
    onSortChange({ prop, order }) {
      if (!prop || !order) {
        this.sortBy = "total_spending";
        this.sortDir = "desc";
      } else {
        this.sortBy = prop;
        this.sortDir = order === "ascending" ? "asc" : "desc";
      }
      this.page = 1;
      this.loadData();
    },
    open(id) { this.$router.push(`/members/${id}`); },
    emptyForm() {
      return {
        fname: "", lname: "", phone: "", email: "",
        birthday: "", sex: 0, status: 1,
        address: "",
        subdistrict_id: 0, subdistrict: "",
        district_id: 0, district: "",
        province_id: 0, province: "",
        postcode: "",
      };
    },
    openCreate() {
      this.form = this.emptyForm();
      this.addrQuery = "";
      this.addrOpen = false;
      this.addrCounter = -1;
      this.showCreate = true;
    },
    onAddrChange() {
      this.addrOpen = true;
      this.addrCounter = -1;
    },
    onAddrArrowDown() {
      if (this.addrCounter < this.filteredAddress.length - 1) {
        this.addrCounter += 1;
      }
    },
    onAddrArrowUp() {
      if (this.addrCounter > 0) {
        this.addrCounter -= 1;
      }
    },
    onAddrEnter() {
      if (this.addrCounter >= 0 && this.filteredAddress[this.addrCounter]) {
        this.setAddrResult(this.filteredAddress[this.addrCounter]);
      }
      this.addrCounter = -1;
      this.addrOpen = false;
    },
    setAddrResult(r) {
      this.form.subdistrict_id = r.DistrictID;
      this.form.subdistrict = r.District;
      this.form.district_id = r.CityID;
      this.form.district = r.City;
      this.form.province_id = r.ProvinceID;
      this.form.province = r.Province;
      this.form.postcode = r.Zipcode;
      const sep = r.ProvinceID == 10 ? " เขต" : " อ.";
      this.addrQuery = `${r.District}${sep}${r.City} จ.${r.Province} ${r.Zipcode}`;
      this.addrOpen = false;
    },
    async submitCreate() {
      // Centralised validation — fire one alert and bail. Matches the patterns
      // used in OrderForm.handleSaveOrder() and MemberDetail.save() so error
      // wording stays consistent across the app.
      const errAlert = (key, fallback) => {
        swal.fire({
          title: this.$t(key) || fallback,
          icon: "error",
          buttonsStyling: false,
          confirmButtonClass: "btn btn-danger btn-fill",
        });
      };

      const fname = (this.form.fname || "").trim();
      if (!fname) {
        return errAlert("member_admin.required_fname", "First name is required");
      }

      // Thai mobile: 10 digits starting with 0. Same regex as the rest of
      // the system (OrderForm recipient_phone, MemberDetail.save).
      const phone = (this.form.phone || "").trim();
      if (!/^0\d{9}$/.test(phone)) {
        return errAlert("phone_invalid", "Invalid phone number (10 digits starting with 0)");
      }

      // Email is optional. When supplied, do a basic shape check.
      const email = (this.form.email || "").trim();
      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        return errAlert("member_admin.email_invalid", "Invalid email format");
      }

      // Address line + the four admin levels must all be filled, otherwise
      // the member ends up in the list with no usable shipping address.
      const address = (this.form.address || "").trim();
      if (!address ||
          !this.form.subdistrict || !this.form.district ||
          !this.form.province || !this.form.postcode) {
        return errAlert("member_admin.address_required", "Address is required (use the search box to fill subdistrict / district / province / postcode)");
      }

      this.creating = true;
      try {
        const res = await member.AdminCreate(this.form);
        const created = (res.data && res.data.Data && res.data.Data[0]) || {};
        this.showCreate = false;
        if (created.id) {
          this.$router.push(`/members/${created.id}`);
        } else {
          this.loadData();
        }
      } catch (e) {
        const msg =
          (e.response && e.response.data && (e.response.data.Cause || e.response.data.Msg)) ||
          this.$t("create_failed") ||
          "Failed";
        swal.fire({ title: msg, icon: "error", buttonsStyling: false, confirmButtonClass: "btn btn-danger btn-fill" });
      }
      this.creating = false;
    },
  },
};
</script>

<style>
.autocomplete-results {
  padding: 0;
  margin: 0;
  border: 1px solid #eeeeee;
  height: 120px;
  min-height: 1em;
  max-height: 6em;
  overflow: auto;
}
.autocomplete-result {
  list-style: none;
  text-align: left;
  padding: 4px 6px;
  cursor: pointer;
}
.autocomplete-result:hover,
.autocomplete-result.is-active {
  background-color: #4aae9b;
  color: white;
}

</style>
